// Reconciliation: the one state v1 can state truthfully. Nothing has compared these books with an
// independent source yet, so the page says exactly that, in the served words, for the month asked —
// never "all reconciled", and never an empty list of differences that would read as "nothing
// differed" (plan §3.4).

import { Card, Select } from '@givmo/design-system';
import type { BooksHome } from './books-wire';
import type { BooksWords } from './books-words';
import { BooksLoading, BooksPageShell, BooksStateCard, StatusLabel } from './books-ui';
import { useBooksNavigate, useBooksSearch } from './books-links';
import { useBooksReconciliation } from './books-queries';
import { periodName } from './books-format';

/** The one state this page explains in its own words; any other is told in the served message alone. */
const NO_COMPARISON_RECORDED = 'no_comparison_recorded';

export function ReconciliationPage({ home, words }: { home: BooksHome; words: BooksWords }) {
  const read = useBooksSearch();
  const navigate = useBooksNavigate();
  const listed = home.periods.map((period) => period.period);
  const asked = read('period');
  const period = asked !== undefined && listed.includes(asked) ? asked : (listed.at(-1) ?? null);
  const state = useBooksReconciliation(home.org_ref, period);

  return (
    <BooksPageShell title="Reconciliation" testId="books-reconciliation">
      {period === null ? (
        <BooksStateCard
          testId="books-reconciliation-none"
          title="No months yet"
          description="There is nothing to compare until the books hold a month."
        />
      ) : (
        <>
          <div className="flex items-center gap-8">
            <span className="font-sans text-s13 tracking-givmo text-text-secondary">Month</span>
            <Select
              appearance="saas"
              aria-label="Month"
              value={period}
              options={[...listed].reverse().map((value) => ({ value, label: periodName(value) }))}
              onValueChange={(next) =>
                next ? navigate({ page: 'reconciliation' }, { period: next }) : undefined
              }
              className="min-w-[180px]"
              testId="books-reconciliation-month"
            />
          </div>
          {state.isPending ? (
            <BooksLoading>Loading this month…</BooksLoading>
          ) : state.isError ? (
            <BooksStateCard
              testId="books-reconciliation-unavailable"
              title="This month’s reconciliation can’t be read right now"
            />
          ) : (
            <Card className="flex flex-col gap-12 px-24 py-24" testId="books-reconciliation-state">
              <div className="flex flex-wrap items-center gap-12">
                <h2 className="font-sans text-s16 font-w600 tracking-givmo text-text-strong">
                  {periodName(state.data.period)}
                </h2>
                <StatusLabel status={state.data.period_status} words={words} />
              </div>
              <p
                className="font-sans text-s15 tracking-givmo text-text"
                data-testid="books-reconciliation-message"
              >
                {state.data.message}
              </p>
              {state.data.state === NO_COMPARISON_RECORDED ? (
                <p
                  className="max-w-[620px] font-sans text-s13 tracking-givmo text-text-secondary"
                  data-testid="books-reconciliation-explained"
                >
                  A comparison is recorded when these books are compared with a record kept outside
                  them, such as a bank statement. None has been recorded for this month, so Books
                  can’t say whether anything differs.
                </p>
              ) : null}
            </Card>
          )}
        </>
      )}
    </BooksPageShell>
  );
}
